'use client';

import { FC, useEffect, useMemo, useState } from 'react';

import { useAuthentication } from '@ravago/shared/authentication';
import { PortalUsersUtils } from '@ravago/shared/next-data-access/client';
import { Alert } from '@ravago/shared/radiance/components/alert/components/Alert/Alert';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Flyout, FlyoutActions, FlyoutContent, FlyoutHeader } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { Image } from '@ravago/shared/radiance/components/image/components/Image/Image';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { List } from '@ravago/shared/radiance/components/list/components/List/List';
import { ListItem } from '@ravago/shared/radiance/components/list/components/ListItem/ListItem';
import { Menu } from '@ravago/shared/radiance/components/menu/components/Menu/Menu';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import Format from '@ravago/shared/radiance/utils/format/format';
import { useTranslations } from '@ravago/shared/translations/client';

import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { usePortalUsers } from '../../../../hooks/use-portal-users';

interface InviteForm {
   firstName: string;
   lastName: string;
   email: string;
}

interface Props {
   open: boolean;
   contactEmail: string;
   locale: string;
   onClose: (userInvited: boolean) => void;
}

const defaultValues: InviteForm = {
   firstName: '',
   lastName: '',
   email: ''
};

export const InviteColleagueForm: FC<Props> = ({ open, contactEmail, locale, onClose }) => {
   const { user } = useAuthentication();
   const { portalUsers } = usePortalUsers();
   const translate = useTranslations();

   const [step, setStep] = useState<1 | 2>(1);
   const [emailProviders, setEmailProviders] = useState<string[]>([]);
   const [selectedEmailProvider, setSelectedEmailProvider] = useState<string>('');
   const [providerSelectorOpen, setProviderSelectorOpen] = useState(false);
   const [inviteFailed, setInviteFailed] = useState(false);
   const [inviteFailedMessage, setInviteFailedMessage] = useState<string>();

   const {
      control,
      handleSubmit,
      reset,
      formState: { isValid }
   } = useForm<InviteForm, unknown, InviteForm>({
      defaultValues,
      mode: 'onBlur'
   });

   const { phone } = useBreakpoint();

   const onSubmit: SubmitHandler<InviteForm> = ({ firstName, lastName, email }) => {
      if (!user) return;

      PortalUsersUtils.addPortalUser({
         firstName,
         lastName,
         email: `${email}@${selectedEmailProvider}`,
         locale,
         ravagoCustomerId: user.ravagoCustomerId,
         groups: ['CUSTOMER_PORTAL']
      })
         .then(({ success, message }) => {
            if (!success) {
               setInviteFailed(true);
               setInviteFailedMessage(message);
               return;
            }

            setStep(2);
         })
         .catch(() => {
            setInviteFailed(true);
            setInviteFailedMessage(undefined);
         });
   };

   const handleClose = () => {
      onClose(step === 2);

      setStep(1);
      reset();
   };

   const hasMultipleEmailProviders = useMemo(() => emailProviders.length > 1, [emailProviders]);

   const emailTrailingIcon = useMemo(() => {
      if (hasMultipleEmailProviders) {
         return providerSelectorOpen ? 'chevron-up' : 'chevron-down';
      }

      return undefined;
   }, [hasMultipleEmailProviders, providerSelectorOpen]);

   useEffect(() => {
      if (!user) return;

      const defaultProvider = user?.mail?.split('@')?.[1] || '';

      if (defaultProvider && !emailProviders.includes(defaultProvider)) {
         setEmailProviders((prev) => [...prev, defaultProvider]);

         if (!selectedEmailProvider) setSelectedEmailProvider(defaultProvider);
      }
   }, [user]);

   useEffect(() => {
      if (!portalUsers?.length) return;

      const providers = portalUsers
         .map((portalUser) => portalUser.email.split('@')[1])
         .filter((provider, index, self) => provider && self.indexOf(provider) === index);

      setEmailProviders((prev) => [...new Set([...prev, ...providers])]);
   }, [portalUsers]);

   return (
      <Flyout position="right" width={phone ? '360px' : '400px'} open={open} closeOnOutsideClick onFlyoutClose={handleClose}>
         <FlyoutHeader>{translate('portal-users.invite-form-title')}</FlyoutHeader>

         <FlyoutContent>
            {step === 1 && (
               <Box direction="column" gap="md">
                  {inviteFailed && (
                     <Alert variant="error" headline={translate('portal-users.invite-form-failed-title')}>
                        {inviteFailedMessage
                           ? translate(`portal-users.${inviteFailedMessage}`)
                           : translate('portal-users.invite-form-failed-description')}
                     </Alert>
                  )}

                  <Controller
                     name="firstName"
                     control={control}
                     rules={{
                        required: translate('form-validation.required', { field: translate('form-field.first-name') }),
                        minLength: {
                           value: 2,
                           message: translate('form-validation.min-length', {
                              field: translate('form-field.first-name'),
                              value: 2
                           })
                        },
                        maxLength: {
                           value: 40,
                           message: translate('form-validation.max-length', {
                              field: translate('form-field.first-name'),
                              value: 40
                           })
                        }
                     }}
                     render={({ field: { ref, ...field }, fieldState: { error } }) => (
                        <InputField
                           {...field}
                           inputRef={ref}
                           id="invite-first-name"
                           label={translate('portal-users.invite-form-first-name')}
                           autoFocus
                           error={!!error}
                           errorMessage={error?.message}
                        />
                     )}
                  />

                  <Controller
                     name="lastName"
                     control={control}
                     rules={{
                        required: translate('form-validation.required', { field: translate('form-field.last-name') }),
                        minLength: {
                           value: 2,
                           message: translate('form-validation.min-length', {
                              field: translate('form-field.last-name'),
                              value: 2
                           })
                        },
                        maxLength: {
                           value: 40,
                           message: translate('form-validation.max-length', {
                              field: translate('form-field.last-name'),
                              value: 40
                           })
                        }
                     }}
                     render={({ field: { ref, ...field }, fieldState: { error } }) => (
                        <InputField
                           {...field}
                           inputRef={ref}
                           id="invite-last-name"
                           label={translate('portal-users.invite-form-last-name')}
                           error={!!error}
                           errorMessage={error?.message}
                        />
                     )}
                  />

                  <Controller
                     name="email"
                     control={control}
                     rules={{
                        required: translate('form-validation.required', { field: translate('form-field.email') }),
                        minLength: {
                           value: 2,
                           message: translate('form-validation.min-length', {
                              field: translate('form-field.email'),
                              value: 2
                           })
                        },
                        maxLength: {
                           value: 40,
                           message: translate('form-validation.max-length', {
                              field: translate('form-field.email'),
                              value: 40
                           })
                        },
                        pattern: {
                           value: /^[a-zA-Z0-9._%+-]+$/,
                           message: translate('form-validation.invalid-email')
                        }
                     }}
                     render={({ field: { ref, ...field }, fieldState: { error } }) => (
                        <Menu
                           content={
                              <div className="overflow-y-auto">
                                 <List role="listbox">
                                    {emailProviders.map((provider) => (
                                       <ListItem
                                          key={provider}
                                          attributes={{ cy: Format('dropdown-option', provider) }}
                                          role="option"
                                          onClick={() => {
                                             setSelectedEmailProvider(provider);
                                             setProviderSelectorOpen(false);
                                          }}
                                       >
                                          <Box gap="sm" items="center">
                                             <Typography whiteSpace="nowrap">{provider}</Typography>
                                          </Box>
                                       </ListItem>
                                    ))}
                                 </List>
                              </div>
                           }
                           open={providerSelectorOpen}
                           maxHeight
                           width="fill"
                           closeOnOutsideClick={false}
                        >
                           <InputField
                              {...field}
                              inputRef={ref}
                              id="invite-email"
                              label={translate('portal-users.invite-form-email')}
                              suffix={`@${selectedEmailProvider}`}
                              trailingIcon={emailTrailingIcon}
                              error={!!error}
                              errorMessage={error?.message}
                              onSuffixClick={hasMultipleEmailProviders ? () => setProviderSelectorOpen((prev) => !prev) : undefined}
                              onTrailingIconClick={hasMultipleEmailProviders ? () => setProviderSelectorOpen((prev) => !prev) : undefined}
                              onBlur={() => {
                                 field.onBlur();
                                 setProviderSelectorOpen(false);
                              }}
                           />
                        </Menu>
                     )}
                  />

                  <Alert variant="info">{translate('portal-users.invite-form-info', { email: contactEmail })}</Alert>
               </Box>
            )}
            {step === 2 && (
               <Box direction="column">
                  <Image src="/illustrations/invite-a-user-success.svg" alt="" title="" />
                  <Typography variant="heading-4">{translate('portal-users.invite-form-success-title')}</Typography>
                  <Typography variant="body">{translate('portal-users.invite-form-success-description')}</Typography>
                  <Typography variant="body">{translate('portal-users.invite-form-success-end-note')}</Typography>
               </Box>
            )}
         </FlyoutContent>

         <FlyoutActions>
            {step === 1 && (
               <>
                  <Button trailingIcon="long-arrow-right" disabled={!isValid} onClick={handleSubmit(onSubmit)}>
                     {translate('portal-users.invite-form-submit')}
                  </Button>
                  <Button variant="secondary" onClick={handleClose}>
                     {translate('portal-users.invite-form-cancel')}
                  </Button>
               </>
            )}
            {step === 2 && (
               <>
                  <Button
                     variant="secondary"
                     leadingIcon="plus"
                     onClick={() => {
                        setStep(1);
                        reset();
                     }}
                  >
                     {translate('portal-users.invite-form-send-another')}
                  </Button>
                  <Button variant="secondary" onClick={handleClose}>
                     {translate('portal-users.invite-form-close')}
                  </Button>
               </>
            )}
         </FlyoutActions>
      </Flyout>
   );
};
